/**
 * Application timezone: Indian Standard Time (Asia/Kolkata).
 * All date/time display and "today" defaults use this timezone.
 */
export const APP_TIMEZONE = 'Asia/Kolkata';

const locale = 'en-IN';

/**
 * Format a date string for display (date only) in IST.
 */
export function formatDate(dateString: string | undefined | null): string {
  if (dateString == null || dateString === '') return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(locale, { timeZone: APP_TIMEZONE });
}

/**
 * Format date and optional time for display in IST.
 * If timeString is provided (e.g. "14:30:00"), it is appended to dateString for parsing.
 */
export function formatDateTime(dateString: string | undefined | null, timeString?: string): string {
  if (dateString == null || dateString === '') return '-';
  const date = timeString
    ? new Date(dateString + 'T' + timeString)
    : new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const datePart = date.toLocaleDateString(locale, { timeZone: APP_TIMEZONE });
  const timePart = date.toLocaleTimeString(locale, {
    timeZone: APP_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: dateString?.length > 10 ? '2-digit' : undefined,
  });
  return timeString != null && timeString !== '' ? `${datePart} ${timePart}` : datePart;
}

/**
 * Format a datetime string as date + time in IST (single string from API).
 */
export function formatDateTimeString(dateTimeString: string | undefined | null): string {
  if (dateTimeString == null || dateTimeString === '') return '-';
  const date = new Date(dateTimeString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString(locale, {
    timeZone: APP_TIMEZONE,
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/**
 * Return today's date in IST as YYYY-MM-DD (for form defaults).
 */
export function getTodayISO(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: APP_TIMEZONE });
}

/**
 * Return current time in IST as HH:MM (for form defaults).
 */
export function getNowTimeHHMM(): string {
  return new Date().toLocaleTimeString('en-GB', {
    timeZone: APP_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Return date in IST as YYYY-MM-DD for a given number of days in the past (0 = today).
 */
export function getPastDateISO(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toLocaleDateString('en-CA', { timeZone: APP_TIMEZONE });
}

/** Keep call_date_from <= call_date_to for YYYY-MM-DD filter inputs. */
export function normalizeCallDateRange<F extends { call_date_from?: string; call_date_to?: string }>(
  prev: F,
  field: 'call_date_from' | 'call_date_to',
  value: string | undefined
): F {
  const next = { ...prev, [field]: value || undefined } as F;
  const from = next.call_date_from;
  const to = next.call_date_to;
  if (from && to && from > to) {
    if (field === 'call_date_from') {
      return { ...next, call_date_to: from };
    }
    return { ...next, call_date_from: to };
  }
  return next;
}
